import * as React from 'react';
import {useSelector} from 'react-redux';

import {Assignables} from 'nutshell-core/assignables';
import type {CoreEntityType} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {ListViewLoadingBars} from 'shells/list-view/list-view-loading-bars';
import {Popover} from 'shells/popover/popover';

import {getShareTypeFromFilter} from '../../filtering/share-modal/filter-share-button';
import {FilterShareModalWrapper} from '../../filtering/share-modal/filter-share-modal-wrapper';

import {getVisibleAndPopoverNavItems} from './helpers';
import {NavItemWrapper} from './nav-item/nav-item-wrapper';
import type {SidebarSectionType, NavItemObj, ReduxSavedFilters} from './types';
import {VisibleNavItems} from './visible-nav-items';

import styles from './sidebar-section.module.css';

const NUM_FIRST_VISIBLE_ITEMS = 5;

export type Props = {
    header?: string | React.ReactNode;
    entityType: CoreEntityType;
    type: SidebarSectionType;
    navItems: NavItemObj[];
    isLoading?: boolean;
    listsAreImmutable: boolean;
    searchParams: any;
    searchNameQuery: string | null | undefined;
    onOpenModal: (navItem: NavItemObj) => void;
    savedFilters?: ReduxSavedFilters;
};

type ShareModalState = {
    isOpen: boolean;
    navItem: NavItemObj | null | undefined;
};

export function SidebarSection(props: Props) {
    const [shareModalState, setShareModalState] = React.useState<ShareModalState>({
        isOpen: false,
        navItem: null,
    });
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const popoverAnchor = React.useRef();

    const selectedSavedFilter =
        props.savedFilters &&
        shareModalState.navItem &&
        shareModalState.navItem.id &&
        props.savedFilters[shareModalState.navItem.id];

    const assignables = useSelector(Assignables.getAllEnabled);
    const activeNavItemId = props.searchParams.list || props.searchParams.audience;
    const {visibleNavItems, popoverNavItems} = getVisibleAndPopoverNavItems(
        props.navItems,
        activeNavItemId,
        NUM_FIRST_VISIBLE_ITEMS
    );

    return (
        // @ts-expect-error
        <div ref={popoverAnchor}>
            {props.header && <div className={styles.header}>{props.header}</div>}
            <div className='flex flex-dir-col'>
                {props.isLoading ? (
                    <ListViewLoadingBars
                        barHeight={33}
                        spacing={4}
                        numBars={props.type === 'draft' ? 1 : 4}
                        padding={0}
                        backgroundColor='sidebarListLoading'
                    />
                ) : (
                    <VisibleNavItems
                        navItems={visibleNavItems}
                        type={props.type}
                        entityType={props.entityType}
                        listsAreImmutable={props.listsAreImmutable}
                        onOpenModal={props.onOpenModal}
                        onClosePopover={() => setIsPopoverOpen(false)}
                        onShare={
                            props.type === 'saved'
                                ? (navItem) =>
                                      setShareModalState({
                                          isOpen: true,
                                          navItem,
                                      })
                                : undefined
                        }
                        searchNameQuery={props.searchNameQuery}
                    />
                )}
            </div>
            {popoverNavItems.length > 0 && (
                <div className='px-8 py-4'>
                    <Button
                        size='tiny'
                        variant='text-primary'
                        onClick={() => setIsPopoverOpen(true)}
                        noPadding={true}
                    >
                        View {popoverNavItems.length} more
                    </Button>
                </div>
            )}
            {isPopoverOpen && (
                <Popover
                    anchor={popoverAnchor.current}
                    location='right'
                    // @ts-expect-error
                    onClose={() => setIsPopoverOpen(false)}
                    onBlur={() => setIsPopoverOpen(false)}
                    hideNub={true}
                    noDefaultStyling={true}
                    offsetPosition={12}
                >
                    <div className={styles.popover}>
                        {popoverNavItems.map((navItem) => (
                            <NavItemWrapper
                                key={`${props.type}-${navItem.id || navItem.name}`}
                                entityType={props.entityType}
                                type={props.type}
                                navItem={navItem}
                                onEdit={() => props.onOpenModal(navItem)}
                                onClick={() => setIsPopoverOpen(false)}
                                onShare={() =>
                                    props.type === 'saved'
                                        ? setShareModalState({
                                              isOpen: true,
                                              navItem,
                                          })
                                        : undefined
                                }
                                searchNameQuery={props.searchNameQuery}
                            />
                        ))}
                    </div>
                </Popover>
            )}
            {shareModalState.isOpen && selectedSavedFilter && (
                <FilterShareModalWrapper
                    savedFilter={selectedSavedFilter}
                    shareType={getShareTypeFromFilter(selectedSavedFilter)}
                    isOpen={shareModalState.isOpen}
                    onClose={() => setShareModalState({isOpen: false, navItem: null})}
                    // @ts-expect-error
                    assignables={assignables}
                    title='Share list'
                />
            )}
        </div>
    );
}
